/**
 * PreviewActions — the bottom action pair from the program preview design
 * (orange-outline secondary + brand-purple primary). Both actions are
 * optional so the component is reusable for the learner runtime which
 * typically only needs a "Continue" pill.
 */

import React from 'react';

export interface PreviewActionsProps {
  /** Secondary (orange-outline) button. Defaults to "Go back" copy when label omitted. */
  back?: {
    label?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
  };
  /** Primary (brand-purple) button. */
  primary?: {
    label?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    busy?: boolean;
  };
}

function ArrowLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14 7l-5 5 5 5M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="animate-spin" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
      <path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export default function PreviewActions({ back, primary }: PreviewActionsProps) {
  return (
    <div className="flex items-center justify-center gap-3 flex-wrap py-3">
      {back && (
        <button
          type="button"
          onClick={back.onClick}
          disabled={back.disabled}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold text-secondary border border-secondary bg-transparent hover:bg-secondary/5 active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
        >
          {back.icon ?? <ArrowLeftIcon />}
          {back.label ?? 'Go back'}
        </button>
      )}
      {primary && (
        <button
          type="button"
          onClick={primary.onClick}
          disabled={primary.disabled || primary.busy}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold text-white hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          style={{ background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)` }}
        >
          {primary.busy ? <SpinnerIcon /> : (primary.icon ?? <CheckIcon />)}
          {primary.label ?? 'Continue'}
        </button>
      )}
    </div>
  );
}
